import { NumberField as BaseNumberField } from '@base-ui/react/number-field'
import React from 'react'
import styled, { css } from 'styled-components'
import { ChevronDown } from 'sutra/components/Icons'
import { accents, brand, gray } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'
import { radii } from 'sutra/tokens/radii'

export type NumberFieldSize = 'sm' | 'md'

type RootProps = Pick<
  BaseNumberField.Root.Props,
  | 'value'
  | 'defaultValue'
  | 'onValueChange'
  | 'min'
  | 'max'
  | 'step'
  | 'disabled'
  | 'name'
  | 'id'
  | 'className'
  | 'style'
>

export interface NumberFieldProps
  extends
    RootProps,
    Omit<
      React.ComponentPropsWithoutRef<'input'>,
      keyof RootProps | 'size' | 'type' | 'onChange'
    > {
  // Decimal places the value is rounded to and displayed with.
  precision?: number
  // 'sm' (30px, the default) or 'md' (40px), matching Select's sizes.
  size?: NumberFieldSize
}

// Group separators are off so 12000 reads "12000", as the antd field it
// replaces showed it.
const baseFormat: Intl.NumberFormatOptions = { useGrouping: false }

const sizeStyles = {
  sm: css`
    height: 30px;
    font-size: 13px;
  `,
  md: css`
    height: 40px;
    font-size: 14px;
  `,
}

const Root = styled(BaseNumberField.Root)<{ $size: NumberFieldSize }>`
  position: relative;
  display: inline-flex;
  align-items: stretch;
  box-sizing: border-box;
  width: 90px;
  ${({ $size }) => sizeStyles[$size]}
  background: ${brand.white};
  border: 1px solid ${gray[300]};
  border-radius: ${radii.sm}px;
  transition:
    background-color ${motion.duration.base}ms ${motion.ease},
    border-color ${motion.duration.base}ms ${motion.ease},
    box-shadow ${motion.duration.base}ms ${motion.ease};

  &:hover:not([data-disabled]):not(:focus-within) {
    background: ${gray[100]};
  }

  &:focus-within {
    border-color: ${brand.stillNight[800]};
    box-shadow: 0 0 0 4px rgba(40, 54, 79, 0.16);
  }

  &[data-disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const Input = styled(BaseNumberField.Input)`
  flex: 1;
  min-width: 0;
  padding: 0 10px;
  font: inherit;
  font-family: 'Poppins', system-ui, sans-serif;
  color: ${brand.stillNight[800]};
  background: transparent;
  border: none;
  border-radius: ${radii.sm}px;
  outline: none;

  &::placeholder {
    color: ${gray[400]};
  }

  &:disabled {
    cursor: not-allowed;
  }
`

// Overlays the right edge, only while the field is hovered or focused, so the
// number keeps the full width the way a bare input would.
const Steppers = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  width: 20px;
  opacity: 0;
  transition: opacity ${motion.duration.fast}ms ${motion.ease};

  ${Root} :hover:not([data-disabled]) &,
  ${Root}:focus-within & {
    opacity: 1;
  }
`

const stepperStyles = css`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: ${gray[500]};
  background: ${gray[50]};
  border: none;
  cursor: pointer;

  &:hover:not([data-disabled]) {
    color: ${brand.stillNight[800]};
    background: ${gray[200]};
  }

  &[data-disabled] {
    cursor: not-allowed;
    opacity: 0.4;
  }

  &:focus-visible {
    outline: 2px solid ${accents.reflectionBlue[500]};
    outline-offset: -2px;
  }

  svg {
    width: 10px;
    height: 10px;
  }
`

const Increment = styled(BaseNumberField.Increment)`
  ${stepperStyles}
  border-top-right-radius: ${radii.sm - 1}px;

  svg {
    transform: rotate(180deg);
  }
`

const Decrement = styled(BaseNumberField.Decrement)`
  ${stepperStyles}
  border-bottom-right-radius: ${radii.sm - 1}px;
`

// `ref` reaches the <input>, so react-hook-form's focus-on-error and a Field
// label's `htmlFor` both land where a caller expects. `className`/`style`
// reach the outer box, which is what a `styled(NumberField)` width override
// has to size. Empty is `null`: pass `value={x ?? null}`, never `undefined`,
// or the field flips between controlled and uncontrolled.
export const NumberField = React.forwardRef<HTMLInputElement, NumberFieldProps>(
  (
    {
      value,
      defaultValue,
      onValueChange,
      min,
      max,
      step,
      disabled,
      name,
      id,
      className,
      style,
      precision,
      size = 'sm',
      ...inputProps
    },
    ref,
  ) => (
    <Root
      $size={size}
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      name={name}
      id={id}
      className={className}
      style={style}
      format={
        precision === undefined
          ? baseFormat
          : {
              ...baseFormat,
              minimumFractionDigits: precision,
              maximumFractionDigits: precision,
            }
      }
    >
      <Input ref={ref} {...inputProps} />
      <Steppers>
        <Increment>
          <ChevronDown aria-hidden />
        </Increment>
        <Decrement>
          <ChevronDown aria-hidden />
        </Decrement>
      </Steppers>
    </Root>
  ),
)

NumberField.displayName = 'NumberField'
